/**
 * Whether a list's setup is complete enough to be activated (PREC-4537).
 *
 * The app has one setup check (`getListConfigurationStatus`, which disables the Activate toggle and
 * tells the user which step is still missing) and the API had none for an ordinary activation: a
 * direct `PATCH list/:id { status: "ACTIVE" }`, and the MCP tool, could start a list the app never
 * offers to start. This module is that one answer, shared by the web adapter, the API update
 * service and the MCP tools.
 *
 * The input is the list's own fields the decision reads — the source type, which source relations
 * hold rows, the first custom-event request's confirmation, the tracker signal config, the
 * find-person settings and whether the list already holds leads — never a whole web or Sequelize
 * model.
 *
 * The refusal codes are the API's error codes (`Errors`), so a caller turns a reason into the
 * exception or the user-facing sentence it already has one for.
 */

/** The source types the setup decision names, as both apps' `ListSourceType` spells them. */
export const LIST_SETUP_SOURCE_TYPES = {
	CUSTOM_EVENTS: "CUSTOM_EVENTS",
	TRACKER: "TRACKER",
	MAPS: "MAPS",
	JOBS: "JOBS",
	PARALLEL_AI: "PARALLEL_AI",
} as const;

/** The entity types that need find-person settings, as `CsvEnrichmentType` spells them. */
export const LIST_SETUP_ENTITY_TYPES = {
	ORGANIZATION: "ORGANIZATION",
} as const;

/** What an empty source step means for the user (PREC-4118). */
export type ListSourceState = "PROVIDED" | "MISSING" | "MANUAL_ONLY";

/**
 * How many rows each source relation holds, under the name the list payload carries the array
 * (`list.jobs?.length ?? 0`). `customEvents` is the list's scrape-request history, read by the
 * custom-event flags below; `csvEnrichments` is both a source of its own and the file a scrape
 * delivers.
 */
export interface ListSetupSources {
	jobs?: number | null;
	maps?: number | null;
	liCompanyActivity?: number | null;
	liPeople?: number | null;
	liEvents?: number | null;
	liProfileVisits?: number | null;
	liPosts?: number | null;
	csvEnrichments?: number | null;
	parallelAi?: number | null;
	liSn?: number | null;
	companyEnriches?: number | null;
	customEvents?: number | null;
}

/** The fields of a list the setup decision reads. */
export interface ListSetupView {
	sourceType: string;
	entityType?: string | null;
	/** The row counts of the list's own source relations — see {@link ListSetupSources}. */
	sources?: ListSetupSources | null;
	/** Whether the first custom-event scrape request has been confirmed. */
	firstCustomEventConfirmed?: boolean | null;
	/** The tracker signal config's id; a TRACKER list without one has no signals yet. */
	signalConfigId?: number | null;
	/** Whether the first Apollo config carries the find-person settings. */
	findPersonConfigured?: boolean | null;
	/** Whether the list already holds leads (the counts `listHasLeads` sums on the web). */
	hasLeads?: boolean | null;
}

/** Why activation refuses a list: one stable code per blocking setup step. */
export type ListSetupRefusalReason =
	| "LIST_SETUP_SCRAPE_NOT_REQUESTED"
	| "LIST_SETUP_SCRAPE_ONGOING"
	| "LIST_SETUP_SCRAPE_UNCONFIRMED"
	| "LIST_SETUP_SOURCE_MISSING"
	| "LIST_SETUP_SOURCE_MANUAL_ONLY"
	| "LIST_SETUP_SIGNALS_MISSING"
	| "LIST_SETUP_FIND_PERSON_MISSING";

export type ListSetupDecision =
	| { allowed: true }
	| { allowed: false; reason: ListSetupRefusalReason };

/** The refused half of a decision, with the code naming the step to finish. */
export interface ListSetupRefusal {
	allowed: false;
	reason: ListSetupRefusalReason;
}

/** Narrows a decision to its refusal (the API's `strictNullChecks: false` does not narrow it). */
export function isListSetupRefused(decision: ListSetupDecision): decision is ListSetupRefusal {
	return !decision.allowed;
}

/** The setup flags every list surface renders, plus the activation decision they add up to. */
export interface ListSetupStatus {
	findPersonAtCompanyMissing: boolean;
	sourceMissing: boolean;
	sourceManualOnly: boolean;
	scrapingNotConfirmed: boolean;
	scrapingNotRequested: boolean;
	scrapingOngoing: boolean;
	signalsMissing: boolean;
	/** What an empty source step means, when the source step is the one missing. */
	sourceState: ListSourceState;
	activation: ListSetupDecision;
}

const count = (value?: number | null): number => Number(value ?? 0) || 0;

/**
 * Whether the list's own source relation is empty, per its source type — the switch
 * `listSourceIsEmpty` owns on the web. A source type that is not listed (TRACKER, EMPTY,
 * LI_PROFILE, SMART_LIST …) has no source array to be missing: TRACKER is judged by its signal
 * config instead, and the rest carry no source step at all.
 */
export function listSetupSourceIsEmpty(
	sourceType: string,
	sources?: ListSetupSources | null
): boolean {
	switch (sourceType) {
		case "JOBS":
			return count(sources?.jobs) === 0;
		case "MAPS":
			return count(sources?.maps) === 0;
		case "LI_COMPANY_ACTIVITY":
			return count(sources?.liCompanyActivity) === 0;
		case "LI_PEOPLE":
			return count(sources?.liPeople) === 0;
		case "LI_EVENTS":
			return count(sources?.liEvents) === 0;
		case "LI_PROFILE_VISITS":
			return count(sources?.liProfileVisits) === 0;
		case "LI_POST":
			return count(sources?.liPosts) === 0;
		case "CSV_ENRICHMENTS":
		case "JOB_CHANGE":
			return count(sources?.csvEnrichments) === 0;
		case "PARALLEL_AI":
			return count(sources?.parallelAi) === 0;
		case "LI_SN":
			return count(sources?.liSn) === 0;
		case "COMPANY_ENRICH":
			return count(sources?.companyEnriches) === 0;
		default:
			return false;
	}
}

/**
 * An empty source step means one of two different things (PREC-4118), and only one of them is a
 * configuration problem: a list that already holds hand-added leads is being worked, not left
 * unfinished.
 */
export function listSetupSourceState(view: ListSetupView): ListSourceState {
	if (!listSetupSourceIsEmpty(view.sourceType, view.sources)) {
		return "PROVIDED";
	}

	return view.hasLeads ? "MANUAL_ONLY" : "MISSING";
}

/**
 * The setup flags, in the order the app has always checked them, and the activation decision they
 * add up to: every one of them blocks activation (the Activate toggle is disabled for each), and
 * the first one that holds is the step the user is told to finish.
 */
export function listSetupStatus(view: ListSetupView): ListSetupStatus {
	const customEventCount = count(view.sources?.customEvents);
	const csvEnrichmentCount = count(view.sources?.csvEnrichments);
	const isCustomEvents = view.sourceType === LIST_SETUP_SOURCE_TYPES.CUSTOM_EVENTS;

	const scrapingNotRequested = isCustomEvents && customEventCount === 0;
	const scrapingOngoing =
		!scrapingNotRequested && isCustomEvents && customEventCount > 0 && csvEnrichmentCount === 0;
	const scrapingNotConfirmed =
		!scrapingNotRequested &&
		!scrapingOngoing &&
		isCustomEvents &&
		customEventCount > 0 &&
		csvEnrichmentCount > 0 &&
		view.firstCustomEventConfirmed !== true;

	const sourceState = listSetupSourceState(view);
	const sourceMissing = sourceState === "MISSING";
	const sourceManualOnly = sourceState === "MANUAL_ONLY";

	const earlierFlagsClear =
		!scrapingNotRequested &&
		!scrapingOngoing &&
		!scrapingNotConfirmed &&
		!sourceMissing &&
		!sourceManualOnly;

	const signalsMissing =
		earlierFlagsClear &&
		view.sourceType === LIST_SETUP_SOURCE_TYPES.TRACKER &&
		!view.signalConfigId;

	const findPersonAtCompanyMissing =
		earlierFlagsClear &&
		!signalsMissing &&
		(view.entityType === LIST_SETUP_ENTITY_TYPES.ORGANIZATION ||
			view.sourceType === LIST_SETUP_SOURCE_TYPES.MAPS ||
			view.sourceType === LIST_SETUP_SOURCE_TYPES.JOBS ||
			view.sourceType === LIST_SETUP_SOURCE_TYPES.PARALLEL_AI) &&
		view.findPersonConfigured !== true;

	return {
		findPersonAtCompanyMissing,
		sourceMissing,
		sourceManualOnly,
		scrapingNotConfirmed,
		scrapingNotRequested,
		scrapingOngoing,
		signalsMissing,
		sourceState,
		activation: listSetupRefusalReason({
			findPersonAtCompanyMissing,
			sourceMissing,
			sourceManualOnly,
			scrapingNotConfirmed,
			scrapingNotRequested,
			scrapingOngoing,
			signalsMissing,
		}),
	};
}

/** The first blocking step, in the app's order, as an activation decision. */
export function listSetupRefusalReason(
	flags: Omit<ListSetupStatus, "sourceState" | "activation">
): ListSetupDecision {
	if (flags.scrapingNotRequested) {
		return { allowed: false, reason: "LIST_SETUP_SCRAPE_NOT_REQUESTED" };
	}

	if (flags.scrapingOngoing) {
		return { allowed: false, reason: "LIST_SETUP_SCRAPE_ONGOING" };
	}

	if (flags.scrapingNotConfirmed) {
		return { allowed: false, reason: "LIST_SETUP_SCRAPE_UNCONFIRMED" };
	}

	if (flags.sourceMissing) {
		return { allowed: false, reason: "LIST_SETUP_SOURCE_MISSING" };
	}

	if (flags.sourceManualOnly) {
		return { allowed: false, reason: "LIST_SETUP_SOURCE_MANUAL_ONLY" };
	}

	if (flags.signalsMissing) {
		return { allowed: false, reason: "LIST_SETUP_SIGNALS_MISSING" };
	}

	if (flags.findPersonAtCompanyMissing) {
		return { allowed: false, reason: "LIST_SETUP_FIND_PERSON_MISSING" };
	}

	return { allowed: true };
}
